import { BarChart3, Building2, ChevronDown, ChevronRight, Megaphone, Plus, Settings, ShieldCheck, Users } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/lib/auth/authStore";
import { getVisibleNavItems, SUPERADMIN_NAV_GROUPS, type NavItem } from "@/app/layouts/sidebarNavConfig";
import { useCapabilities } from "@/features/capabilities/hooks/useCapabilities";
import { useSwitchFunctionalAccountMutation } from "@/features/auth/hooks/useAuthMutations";
import { useMyBusinessMemberships } from "@/features/businesses/hooks/useBusinesses";
import type { FunctionalAccount, FunctionalAccountType } from "@/features/auth/types";
import { formatNavBadgeCount, useNavBadges } from "@/features/dashboard/hooks/useNavBadges";

// Map Albanian nav label → i18n key (covers all 3 nav configs)
const NAV_LABEL_KEY: Record<string, string> = {
  "Dashboard":           "nav.dashboard",
  "Dashboard Korporativ": "nav.corporateDashboard",
  "Agjenda":             "nav.professionalPanel",
  "Workbench":           "nav.labAdmin",
  "Kërko Mjekun":        "nav.search",
  "Faqja ime":           "nav.patientHome",
  "Paneli im":           "nav.professionalPanel",
  "Terminet e Mia":      "nav.appointments",
  "Oraret":              "nav.appointments",
  "Monitorimi Im":       "nav.monitoring",
  "Monitorimi":          "nav.monitoring",
  "Familja Ime":         "nav.familyCare",
  "Kujdesi familjar":    "nav.familyCare",
  "Historiku Klinik":    "nav.clinicalHistory",
  "Laboratori":          "nav.laboratory",
  "Rezultatet":          "nav.labResults",
  "Imazheritë":          "nav.laboratory",
  "Panel Laborantit":    "nav.labAdmin",
  "Service Hub":         "nav.serviceHub",
  "Inventari":           "nav.inventory",
  "Ofertat e Punës":     "nav.jobs",
  "Sigurimet":           "nav.insurance",
  "Ngjarjet":            "nav.events",
  "Favoritet":           "nav.favorites",
  "Wallet":              "nav.wallet",
  "Portofoli":           "nav.businessWallet",
  "Medimarket":          "nav.medimarket",
  "Shporta":             "nav.cart",
  "Dyqani im":           "nav.seller",
  "Regjistro Biznesin":  "nav.registerBusiness",
  "Blogu im":            "nav.myBlog",
  "Moderimi i Blogut":   "nav.postModeration",
  "Faturat":             "nav.invoices",
  "Mesazhet":            "nav.messages",
  "Departamentet":       "nav.departments",
  "Shtretërit":          "nav.hospitalBeds",
  "Pranimet":            "nav.hospitalAdmissions",
  "Control Room":        "nav.admin",
  "Verifikimi i Mjekëve": "nav.verifyDoctors",
  "Menaxhimi i Reklamave": "nav.adsManagement",
  "Verifikimi Mjekëve":  "nav.admin",
  "Siguria":             "nav.admin",
  "Statistikat":         "nav.admin",
  "Module Toggles":      "nav.serviceHub",
  "Platform Config":     "nav.account",
  "Admin":               "nav.admin",
  "Llogaria":            "nav.account",
  "Bizneset":            "nav.businesses",
  "Klinikat":            "nav.businesses",
  "Klinika Ime":         "nav.myClinic",
  "Template i Raportit": "nav.reportTemplate",
};

export interface SidebarNavProps {
  collapsed?: boolean;
  onNavigate?: () => void;
}

const exactOnlyPaths = new Set(["/app/dashboard", "/app/superadmin"]);

const getActiveMatchScore = (itemTarget: string, currentPathname: string, currentSearch: string): number => {
  const itemUrl = new URL(itemTarget, "http://app.local");
  const itemPathname = itemUrl.pathname.replace(/\/+$/, "") || "/";
  const normalizedCurrentPathname = currentPathname.replace(/\/+$/, "") || "/";
  const itemSearchParams = itemUrl.searchParams;
  const hasTargetSearch = Array.from(itemSearchParams.keys()).length > 0;

  if (itemPathname !== normalizedCurrentPathname) {
    if (exactOnlyPaths.has(itemPathname)) {
      return -1;
    }
    if (!normalizedCurrentPathname.startsWith(`${itemPathname}/`)) {
      return -1;
    }
  }

  if (hasTargetSearch) {
    const currentSearchParams = new URLSearchParams(currentSearch);
    for (const [key, value] of itemSearchParams.entries()) {
      if (currentSearchParams.get(key) !== value) {
        return -1;
      }
    }
    return 3;
  }

  if (exactOnlyPaths.has(itemPathname) && currentSearch) {
    return -1;
  }

  return itemPathname === normalizedCurrentPathname ? 2 : 1;
};

export const SidebarNav = ({ collapsed = false, onNavigate }: SidebarNavProps): JSX.Element => {
  const { systemRole, profileRole, businessRole } = useAuthStore();
  const capabilitiesQuery = useCapabilities();
  const { pathname, search } = useLocation();
  const currentLevel = capabilitiesQuery.data?.institution_level ?? 1;
  const visibleItems = getVisibleNavItems(
    systemRole,
    profileRole,
    businessRole,
    capabilitiesQuery.data,
    pathname,
    currentLevel,
  );
  const navBadgesQuery = useNavBadges(visibleItems.some((item) => Boolean(item.badgeKey)));
  const navBadgeCounts = navBadgesQuery.data?.counts ?? {};
  const { t } = useTranslation();
  const activeItemIndex = visibleItems.reduce(
    (bestMatch, item, index) => {
      const score = getActiveMatchScore(item.to, pathname, search);
      return score > bestMatch.score ? { index, score } : bestMatch;
    },
    { index: -1, score: -1 },
  ).index;
  const isPlatformAdmin = systemRole === "superadmin" || systemRole === "admin";
  const isSuperadmin = systemRole === "superadmin";
  const navLinks = visibleItems.map((item, index) => {
    const labelKey = NAV_LABEL_KEY[item.label];
    const translatedLabel = labelKey ? t(labelKey, { defaultValue: item.label }) : item.label;
    const badgeCount = item.badgeKey ? Number(navBadgeCounts[item.badgeKey] ?? 0) : 0;
    const isActive = index === activeItemIndex;
    return (
      <Link
        key={`${item.to}:${item.label}`}
        to={item.to}
        className={cn(
          "relative flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground",
          isActive && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
          collapsed && "justify-center px-0",
        )}
        aria-current={isActive ? "page" : undefined}
        title={collapsed ? translatedLabel : undefined}
        onClick={onNavigate}
      >
        {item.icon}
        {collapsed ? null : <span className="truncate">{translatedLabel}</span>}
        {badgeCount > 0 ? (
          <span
            className={cn(
              "ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold leading-5 text-primary-foreground ring-1 ring-primary/20",
              collapsed && "absolute -right-1 -top-1 ml-0",
            )}
            aria-label={`${formatNavBadgeCount(badgeCount)} te reja`}
          >
            {formatNavBadgeCount(badgeCount)}
          </span>
        ) : null}
      </Link>
    );
  });

  return (
    <nav className="space-y-1" aria-label="Navigimi kryesor">
      {isSuperadmin ? <SuperadminGroupedNav items={visibleItems} pathname={pathname} search={search} collapsed={collapsed} onNavigate={onNavigate} /> : isPlatformAdmin ? navLinks : navLinks.slice(0, 2)}
      {isPlatformAdmin ? null : <BusinessWorkspaceNav collapsed={collapsed} onNavigate={onNavigate} />}
      {isPlatformAdmin ? null : <ProfileNavGroup collapsed={collapsed} onNavigate={onNavigate} />}
      {isPlatformAdmin ? null : navLinks.slice(2)}
    </nav>
  );
};

const SUPERADMIN_GROUP_ICON = {
  "Aprovime": ShieldCheck,
  "Bizneset dhe Katalogu": Building2,
  "Përmbajtja dhe Promovimi": Megaphone,
  "Përdoruesit dhe Siguria": Users,
  "Financa dhe Module": BarChart3,
  "Sistemi": Settings,
} as const;

const SuperadminGroupedNav = ({
  items,
  pathname,
  search,
  collapsed,
  onNavigate,
}: {
  items: NavItem[];
  pathname: string;
  search: string;
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const navBadgesQuery = useNavBadges(items.some((item) => Boolean(item.badgeKey)));
  const navBadgeCounts = navBadgesQuery.data?.counts ?? {};
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ Aprovime: true });
  const dashboard = items.find((item) => item.label === "Menaxhimi i Platformes");
  const account = items.find((item) => item.label === "Llogaria");
  const renderItem = (item: NavItem): JSX.Element => {
    const labelKey = NAV_LABEL_KEY[item.label];
    const label = labelKey ? t(labelKey, { defaultValue: item.label }) : item.label;
    const badgeCount = item.badgeKey ? Number(navBadgeCounts[item.badgeKey] ?? 0) : 0;
    const isActive = getActiveMatchScore(item.to, pathname, search) >= 0;
    return <Link key={`${item.to}:${item.label}`} to={item.to} className={cn("relative flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground", isActive && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground", collapsed && "justify-center px-0")} aria-current={isActive ? "page" : undefined} title={collapsed ? label : undefined} onClick={onNavigate}>{item.icon}{collapsed ? null : <span className="truncate">{label}</span>}{badgeCount > 0 ? <span className={cn("ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold leading-5 text-primary-foreground ring-1 ring-primary/20", collapsed && "absolute -right-1 -top-1 ml-0")}>{formatNavBadgeCount(badgeCount)}</span> : null}</Link>;
  };

  return <>
    {dashboard ? renderItem(dashboard) : null}
    {SUPERADMIN_NAV_GROUPS.map((group) => {
      const groupItems = group.itemLabels.map((label) => items.find((item) => item.label === label)).filter((item): item is NavItem => Boolean(item));
      if (groupItems.length === 0) return null;
      const isGroupActive = groupItems.some((item) => getActiveMatchScore(item.to, pathname, search) >= 0);
      const isOpen = collapsed ? false : openGroups[group.label] || isGroupActive;
      const Icon = SUPERADMIN_GROUP_ICON[group.label as keyof typeof SUPERADMIN_GROUP_ICON];
      const groupBadgeCount = groupItems.reduce((total, item) => total + (item.badgeKey ? Number(navBadgeCounts[item.badgeKey] ?? 0) : 0), 0);
      return <div key={group.label} className="space-y-1 border-t border-border/70 pt-2">
        <button type="button" className={cn("relative flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground", isGroupActive && "text-foreground", collapsed && "justify-center px-0")} onClick={() => !collapsed && setOpenGroups((current) => ({ ...current, [group.label]: !isOpen }))} aria-expanded={isOpen} title={collapsed ? group.label : undefined}>
          <Icon className="h-4 w-4 shrink-0" />
          {collapsed ? null : <><span className="min-w-0 flex-1 truncate text-left">{group.label}</span>{groupBadgeCount > 0 ? <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold leading-5 text-primary-foreground">{formatNavBadgeCount(groupBadgeCount)}</span> : null}{isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}</>}
          {collapsed && groupBadgeCount > 0 ? <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold leading-5 text-primary-foreground">{formatNavBadgeCount(groupBadgeCount)}</span> : null}
        </button>
        {isOpen ? <div className="ml-5 space-y-1 border-l border-border/70 pl-2">{groupItems.map(renderItem)}</div> : null}
      </div>;
    })}
    {account ? <div className="border-t border-border/70 pt-2">{renderItem(account)}</div> : null}
  </>;
};

const profileItems: Array<{ type: FunctionalAccountType; label: string; previewPath: string }> = [
  { type: "patient", label: "Pacient", previewPath: "/app/dashboard-preview/patient" },
  { type: "doctor", label: "Doktor", previewPath: "/app/dashboard-preview/doctor" },
  { type: "specialist", label: "Specialist", previewPath: "/app/dashboard-preview/specialist" },
  { type: "technician", label: "Teknik", previewPath: "/app/dashboard-preview/technician" },
];

const ProfileNavGroup = ({ collapsed, onNavigate }: SidebarNavProps): JSX.Element => {
  const navigate = useNavigate();
  const { activeFunctionalAccount, availableFunctionalAccounts, profileRole } = useAuthStore();
  const switchMutation = useSwitchFunctionalAccountMutation();
  const [open, setOpen] = useState(false);
  const nonBasicAccounts = availableFunctionalAccounts.filter((account) => account.functional_account_type !== "basic");

  const openProfile = (account: FunctionalAccount, path: string): void => {
    if (account.id === activeFunctionalAccount?.id) {
      navigate(path);
      onNavigate?.();
      return;
    }
    switchMutation.mutate({ functional_account_id: Number(account.id) }, { onSuccess: () => { navigate(path); onNavigate?.(); } });
  };

  const createProfile = (type: FunctionalAccountType): void => {
    navigate(`/app/account?create_profile=${type}`);
    onNavigate?.();
  };

  const studentAvailable = profileRole === "student";

  return (
    <div className="space-y-1 border-t border-border/70 pt-2">
      <button
        type="button"
        className={cn("flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground", collapsed && "justify-center px-0")}
        onClick={() => {
          if (collapsed) {
            navigate("/app/dashboard-preview");
            onNavigate?.();
            return;
          }
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        title={collapsed ? "Profilet" : undefined}
      >
        <Users className="h-4 w-4 shrink-0" />
        {collapsed ? null : <><span className="flex-1 text-left">Profilet</span>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</>}
      </button>
      {open && !collapsed ? (
        <div className="space-y-1 border-l border-border/70 pl-3">
          {profileItems.map((item) => {
            const account = nonBasicAccounts.find((candidate) => candidate.functional_account_type === item.type);
            return account ? (
              <button key={item.type} type="button" disabled={switchMutation.isPending} onClick={() => openProfile(account, item.previewPath)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground disabled:opacity-60">
                <Users className="h-3.5 w-3.5 text-primary" /><span className="flex-1 truncate">{item.label}</span>{account.id === activeFunctionalAccount?.id ? <span className="text-[10px] font-semibold text-primary">Aktiv</span> : null}
              </button>
            ) : (
              <button key={item.type} type="button" onClick={() => createProfile(item.type)} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"><Plus className="h-3.5 w-3.5 text-primary" /><span className="flex-1">Shto {item.label}</span></button>
            );
          })}
          {studentAvailable ? <Link to="/app/dashboard-preview/student" onClick={onNavigate} className="flex h-9 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"><Users className="h-3.5 w-3.5 text-primary" />Student<span className="ml-auto text-[10px] font-semibold text-primary">Aktiv</span></Link> : <button type="button" onClick={() => { navigate("/app/account"); onNavigate?.(); }} className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"><Plus className="h-3.5 w-3.5 text-primary" />Shto Student</button>}
          {nonBasicAccounts.length === 0 ? <p className="px-2 py-1 text-xs text-muted-foreground">Krijoni profilin e parë për të hapur dashboardin provë.</p> : null}
        </div>
      ) : null}
    </div>
  );
};

const BusinessWorkspaceNav = ({ collapsed, onNavigate }: SidebarNavProps): JSX.Element => {
  const { businessRole, profileRole } = useAuthStore();
  const membershipsQuery = useMyBusinessMemberships(!collapsed);
  const [open, setOpen] = useState(false);
  const memberships = membershipsQuery.data ?? [];
  const ownerContextActive = businessRole === "owner";
  const professionalWorkspace = profileRole === "doctor" || profileRole === "specialist" || profileRole === "technician";
  const workspacePath = professionalWorkspace ? "/app/professional/businesses" : "/app/businesses-preview";

  if (collapsed) {
    return <Link to={workspacePath} onClick={onNavigate} title="Bizneset e mia" className="grid h-10 place-items-center rounded-md text-muted-foreground transition hover:bg-secondary hover:text-foreground"><Building2 className="h-4 w-4" /></Link>;
  }

  return (
    <div className="space-y-1 border-t border-border/70 pt-2">
      <button type="button" onClick={() => setOpen((value) => !value)} className="flex h-10 w-full items-center gap-3 rounded-md px-3 text-left text-sm font-medium text-muted-foreground transition hover:bg-secondary hover:text-foreground" aria-expanded={open}>
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">Bizneset e mia</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open ? <div className="ml-5 space-y-1 border-l border-border/70 pl-2">
        <Link to={workspacePath} onClick={onNavigate} className="flex min-h-9 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition hover:bg-secondary hover:text-foreground"><Building2 className="h-3.5 w-3.5" />Dashboardi</Link>
        <Link to="/app/business/register" onClick={onNavigate} className="flex min-h-9 items-center gap-2 rounded-md px-2 text-sm font-medium text-primary transition hover:bg-primary/10"><Plus className="h-3.5 w-3.5" />Shto biznes</Link>
        {membershipsQuery.isLoading ? <div className="h-8 animate-pulse rounded-md bg-muted" /> : null}
        {memberships.map((membership) => <div key={membership.id} className="rounded-md px-2 py-1.5 hover:bg-secondary/70">
          <Link to={professionalWorkspace ? "/app/professional/businesses" : `/app/businesses-preview?business_id=${membership.id}`} onClick={onNavigate} className="flex min-w-0 items-center gap-2 text-sm text-foreground"><Building2 className="h-3.5 w-3.5 shrink-0 text-primary" /><span className="truncate">{membership.name}</span></Link>
          <div className="mt-1 flex items-center gap-3 pl-5 text-xs">
            <Link to={`/businesses/${membership.slug}`} onClick={onNavigate} className="text-muted-foreground hover:text-primary">Pamja publike</Link>
            {ownerContextActive && membership.relationship === "owner" ? <Link to={`/app/business/manage?businessId=${membership.id}&tab=profile`} onClick={onNavigate} className="text-primary hover:underline">Menaxho</Link> : null}
          </div>
        </div>)}
        {!membershipsQuery.isLoading && memberships.length === 0 ? <p className="px-2 py-1 text-xs text-muted-foreground">Nuk ka biznese të lidhura.</p> : null}
      </div> : null}
    </div>
  );
};
